import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate, Navigate } from "react-router-dom";
import { Sparkle, Lock, EnvelopeSimple } from "@phosphor-icons/react";

const BG = "https://images.pexels.com/photos/33257668/pexels-photo-33257668.jpeg";

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign in failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left — navy hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-myb-700">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-br from-myb-700/95 via-myb-800/92 to-myb-900/98" />
        <div className="relative z-10">
          <div className="w-20 h-20 rounded-sm bg-white/5 border border-white/15 flex items-center justify-center mb-6">
            <img src="/myb-logo.png" alt="Morad Yousuf Behbehani" className="w-14 h-14 object-contain" />
          </div>
          <div className="text-[10px] tracking-[0.4em] text-myb-200 uppercase font-semibold">Morad Yousuf</div>
          <div className="font-display text-6xl mt-1 text-white leading-none font-light">Behbehani</div>
          <div className="text-xs text-white/50 mt-3 uppercase tracking-[0.35em] font-medium">Since 1935 · AI Sales Intelligence</div>
        </div>
        <div className="relative z-10 max-w-md">
          <blockquote className="font-display text-2xl text-white/95 leading-snug font-light italic">
            "Nine decades of service. One view of everything we sell."
          </blockquote>
          <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-myb-200 font-semibold">
            Executive Retail Intelligence — Kuwait · KWD
          </div>
        </div>
      </div>

      {/* Right — light form */}
      <div className="flex items-center justify-center p-8 md:p-12 relative bg-background">
        <div className="relative w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-sm bg-myb-500 flex items-center justify-center mb-3">
              <img src="/myb-logo.png" alt="" className="w-12 h-12 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
            </div>
            <div className="text-[10px] tracking-[0.4em] text-myb-500 uppercase font-semibold">Morad Yousuf</div>
            <div className="font-display text-4xl text-foreground leading-none font-light">Behbehani</div>
            <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-[0.3em] font-medium">Since 1935</div>
          </div>

          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Secure Access</div>
            <h1 className="font-display text-4xl lg:text-5xl mt-2 text-foreground font-light">Sign in</h1>
            <p className="text-muted-foreground mt-3 text-sm">
              Enter your assigned email and password to continue.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Email</label>
              <div className="relative">
                <EnvelopeSimple size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-myb-500" />
                <input
                  data-testid="login-email"
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-border focus:border-myb-500 rounded-sm pl-9 pr-3 py-3 text-sm outline-none transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-myb-500" />
                <input
                  data-testid="login-password"
                  type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-border focus:border-myb-500 rounded-sm pl-9 pr-3 py-3 text-sm outline-none transition-colors"
                />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-red-50 border border-red-200 rounded-sm px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit" disabled={busy}
              className="w-full bg-myb-500 text-white hover:bg-myb-600 disabled:opacity-60 rounded-sm py-3 font-medium tracking-wide transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-sm"
            >
              <Sparkle weight="fill" size={16} />
              {busy ? "Signing in…" : "Enter Behbehani Analytica"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
